import { useEffect, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/i18n";
import { API, photoUrl } from "@/lib/site";
import { Reveal, SectionHead } from "./Reveal";

const PATTERN = ["md:row-span-2", "md:col-span-2", "", ""];
const spanFor = (i, total) => {
  const full = total - (total % 4);
  if (i < full) return PATTERN[i % 4];
  const rest = total - full;
  if (i !== total - 1) return "";
  return rest === 1 ? "sm:col-span-2 md:col-span-3" : rest === 2 ? "md:col-span-2" : "";
};
const FALLBACK = ["chupitos", "crowd", "g-a", "g-b", "g-c", "halloween", "antidoto", "g-d", "beso"].map((n) => ({ id: n, url: `/img/${n}.jpg`, caption: "" }));

export const Galeria = () => {
  const { t } = useLang();
  const [view, setView] = useState(null);
  const [photos, setPhotos] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${API}/gallery`)
      .then((r) => { if (Array.isArray(r.data)) setPhotos(r.data); })
      .catch(() => {});
  }, []);

  return (
    <section id="galeria" data-testid="galeria-section" className="bg-tropical-light py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHead id="galeria" eyebrow={t.galeria.eyebrow} title={t.galeria.title} />
          <Reveal><p className="max-w-sm text-jungle/75 lg:text-right">{t.galeria.intro}</p></Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 auto-rows-[300px] grid-flow-row-dense gap-4" data-testid="galeria-grid">
          {photos.map((p, i) => (
            <Reveal key={p.id} delay={(i % 6) * 0.06} className={`${spanFor(i, photos.length)} h-full`}>
              <button
                data-testid={`galeria-item-${i}`}
                onClick={() => setView(p)}
                className="group relative h-full w-full overflow-hidden rounded-[1.5rem] bg-jungle text-left"
              >
                <img src={photoUrl(p.url)} alt={p.caption || `${t.galeria.title} ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                {p.caption && (
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-deep/85 to-transparent px-5 pb-4 pt-12 font-neon text-2xl text-paper" data-testid={`galeria-caption-${i}`}>{p.caption}</span>
                )}
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none shadow-none [&>button]:hidden" data-testid="galeria-lightbox">
          <DialogTitle className="sr-only">{t.galeria.title}</DialogTitle>
          {view && <img src={photoUrl(view.url)} alt={view.caption} className="w-full max-h-[80vh] object-contain rounded-2xl" />}
          {view?.caption && <p className="text-center font-neon text-3xl text-paper">{view.caption}</p>}
          <button onClick={() => setView(null)} data-testid="galeria-lightbox-close" className="absolute -top-3 -right-3 rounded-full bg-mango p-2 text-paper" aria-label="Cerrar"><X size={18} /></button>
        </DialogContent>
      </Dialog>
    </section>
  );
};
